import { Link } from '@backstage/core-components'
import { Avatar, Box, Collapse, IconButton, makeStyles, Theme, Tooltip, Typography } from '@material-ui/core'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import LaunchIcon from '@material-ui/icons/Launch'
import { useMemo, useState } from 'react'

import { Pipeline, Workflow } from '../../../../api'
import { useNow, useWorkflowJobs } from '../../../../state'
import { durationHumanized, relativeTimeTo } from '../../../../util'
import { PIPELINE_GRID_COLUMNS } from '../layout'
import { StatusBadge } from '../StatusBadge'
import { JobList } from './JobList'

const useStyles = makeStyles<Theme>(theme => ({
  row: {
    display: 'grid',
    gridTemplateColumns: PIPELINE_GRID_COLUMNS,
    alignItems: 'center',
    columnGap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: 13,
  },
  pipelineCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  expandButton: {
    padding: 4,
  },
  workflowLink: {
    fontWeight: 500,
  },
  sourceCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  sourceLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  sourceSubject: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
  },
  triggerCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  avatar: {
    width: 28,
    height: 28,
  },
  triggerText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  triggerType: {
    fontWeight: 500,
  },
  triggerSubject: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  actionsCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
  },
  expandedRegion: {
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}))

const formatStart = (iso?: string | null): string => {
  if (!iso) return '—'
  return relativeTimeTo(iso) ?? '—'
}

const formatDuration = (start: string | null | undefined, stop: string | null | undefined, now: Date): string => {
  if (!start) return '—'
  const duration = durationHumanized(start, stop ?? now.toISOString())
  return duration || '—'
}

const truncate = (s: string | undefined, n: number): string => {
  if (!s) return ''
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

const pipelineDisplayName = (pipeline: Pipeline): string => {
  const slug = pipeline.project_slug.split('/').pop() ?? ''
  return slug || 'pipeline'
}

const workflowLink = (workflow: Workflow): string =>
  `https://app.circleci.com/pipelines/${workflow.project_slug}/${workflow.pipeline_number}/workflows/${workflow.id}`

type Props = {
  pipeline: Pipeline
  workflow: Workflow
}

export const PipelineRow = ({ pipeline, workflow }: Props) => {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)
  const now = useNow()
  const { jobs, loading: jobsLoading, error: jobsError } = useWorkflowJobs(expanded ? workflow.id : null)

  const vcs = pipeline.vcs
  const branchOrTag = vcs?.branch ?? vcs?.tag ?? ''
  const shortRev = vcs?.revision ? vcs.revision.slice(0, 7) : ''
  const commitSubject = vcs?.commit?.subject

  const sourceUrl = useMemo(() => {
    const base = vcs?.target_repository_url ?? vcs?.origin_repository_url
    if (!base || !vcs?.revision) return undefined
    // Best-effort: works for github.com, bitbucket.org, gitlab.com.
    return `${base.replace(/\.git$/, '')}/commit/${vcs.revision}`
  }, [vcs])

  const circleCiUrl = useMemo(() => workflowLink(workflow), [workflow])

  const triggerType = pipeline.trigger.type === 'webhook' ? 'Push' : pipeline.trigger.type

  return (
    <>
      <div className={classes.row}>
        <div className={classes.pipelineCell}>
          <IconButton size="small" onClick={() => setExpanded(e => !e)} className={classes.expandButton}>
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
          <Typography variant="body2">
            {pipelineDisplayName(pipeline)}{' '}
            <Typography component="span" color="textSecondary" variant="body2">
              {pipeline.number}
            </Typography>
          </Typography>
        </div>

        <div>
          <StatusBadge status={workflow.status} />
        </div>

        <div className={classes.workflowLink}>
          <Link to={circleCiUrl}>{workflow.name}</Link>
        </div>

        <div className={classes.sourceCell}>
          <div className={classes.sourceLine}>
            {branchOrTag && (
              <Typography variant="body2" component="span" color="primary">
                {branchOrTag}
              </Typography>
            )}
            {shortRev &&
              (sourceUrl ? (
                <Link to={sourceUrl}>{shortRev}</Link>
              ) : (
                <Typography variant="body2" component="span" color="textSecondary">
                  {shortRev}
                </Typography>
              ))}
          </div>
          {commitSubject && (
            <Tooltip title={commitSubject}>
              <Typography variant="caption" component="span" className={classes.sourceSubject}>
                {truncate(commitSubject, 80)}
              </Typography>
            </Tooltip>
          )}
        </div>

        <div className={classes.triggerCell}>
          <Tooltip title={pipeline.trigger.actor.login}>
            <Avatar
              src={pipeline.trigger.actor.avatar_url ?? undefined}
              alt={pipeline.trigger.actor.login}
              className={classes.avatar}
            >
              {pipeline.trigger.actor.login.charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>
          <div className={classes.triggerText}>
            <span className={classes.triggerType}>{triggerType}</span>
            <span className={classes.triggerSubject}>Commit pushed</span>
          </div>
        </div>

        <Typography variant="body2" color="textSecondary">
          {formatStart(workflow.created_at)}
        </Typography>

        <Typography variant="body2" color="textSecondary">
          {formatDuration(workflow.created_at, workflow.stopped_at, now)}
        </Typography>

        <div className={classes.actionsCell}>
          <Tooltip title="Open in CircleCI">
            <IconButton size="small" component="a" href={circleCiUrl} target="_blank" rel="noopener">
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <Collapse in={expanded} unmountOnExit>
        <Box className={classes.expandedRegion}>
          <JobList jobs={jobs} loading={jobsLoading} error={jobsError} />
        </Box>
      </Collapse>
    </>
  )
}
